import axios from "axios"
import helpers from "@/helpers/index.js"
import passConstants from "@/constants/passConstants"
import {
  api,
  apiV6nocache,
  queueApiV1NoCache
} from "@/store/modules/axiousWithCache.js"

/**
 * Retrieves and sets the active pass status for the dashboard from local storage.
 *
 * @returns {string} The current status of the pass.
 *                   Possible values:
 *                   - "active" (default)
 *                   - "both"
 *                   - "ended"
 *                   - "queue"
 */
const getAndSetPassStatusFromLocalStorage = () => {
  const status =
    window?.localStorage?.getItem(
      passConstants.LOCAL_STORAGE_KEYS.DASHBOARD_PASS_STATUS
    ) || "active"
  window.localStorage.setItem(
    passConstants.LOCAL_STORAGE_KEYS.DASHBOARD_PASS_STATUS,
    status
  )
  return status
}

/**
 * Retrieves and sets the active pass type for the dashboard from local storage.
 * @returns {string} The current type of the pass.
 *                  Possible values:
 *                 - "my" (default)
 *                 - "all"
 *                 - "appointments_today"
 *                 - "appointments_awaiting
 *                 - "appointments_future_awaiting"
 */
const getAndSetPassTypesFromLocalStorage = () => {
  const type =
    window?.localStorage?.getItem(
      passConstants.LOCAL_STORAGE_KEYS.DASHBOARD_PASS_TYPE
    ) || "my"
  window.localStorage.setItem(
    passConstants.LOCAL_STORAGE_KEYS.DASHBOARD_PASS_TYPE,
    type
  )
  return type
}

/**
 * Processes a queue pass by adding all necessary computed properties
 * This is a centralized place to add any processing logic for queue passes
 *
 * @param {Object} pass - The queue pass object to process
 * @returns {Object} The processed queue pass
 */
const processQueuePass = (pass) => {
  pass.approved_at = "" // this is done to make sorting icon visible and sorting work
  pass.completed_at = "" // this is done to make sorting icon visible and sorting work
  pass.badge_flags = helpers.getBadgeFlags(pass)
  return pass
}

/**
 * Merges an incoming user object onto the one already in the store without
 * losing identity fields. Some pass-update endpoints (e.g. the pass-history
 * edit-time response) return the user object but leave fields such as
 * `student_sis_id` empty. A plain spread would overwrite the good value with an
 * empty one, blanking the Student Number column.
 *
 * Merge rule per incoming field:
 * - A real (non-empty) value always wins. null/undefined/"" count as empty for
 *   every field; "0" additionally counts as empty for student_sis_id.
 * - An empty value only overwrites the existing one when the key is brand new
 *   (not already present on existingUser), so a key that already holds a real
 *   value is never blanked by an empty payload.
 * - student_sis_id === "0" is the exception: it is skipped entirely (even for a
 *   brand-new key) so the literal "0" can never reach the Student Number column.
 *
 * @param {Object} existingUser - The user object currently in the store.
 * @param {Object} incomingUser - The user object from the latest payload.
 * @returns {Object} The merged user object.
 */
const mergeUserPreservingValues = (existingUser, incomingUser) => {
  // UPDATE_PASS may call this when only one side has a `user` object, so guard
  // against a missing existingUser (object spread of null/undefined is safe and
  // yields {}, but normalizing keeps the intent explicit).
  const merged = { ...(existingUser || {}) }
  if (!incomingUser) {
    return merged
  }
  Object.keys(incomingUser).forEach((key) => {
    // Never copy prototype-affecting keys. A backend payload parsed via
    // JSON.parse can carry an own "__proto__"/"constructor"/"prototype" key, and
    // `merged[key] = value` for those would corrupt the merged object's prototype.
    if (key === "__proto__" || key === "constructor" || key === "prototype") {
      return
    }
    const value = incomingUser[key]
    // The string "0" is a "no value" sentinel for student_sis_id only: the app
    // treats student_sis_id === "0" as an absent number (see CreateUserForm.vue's
    // `student_sis_id != "0"` check). Skip it outright — even when `merged` has
    // no student_sis_id yet — otherwise the literal "0" would fall through the
    // new-key branch below and render in the Student Number column. This is
    // intentionally scoped to student_sis_id so we don't block legitimate "0"
    // updates on other string fields. Numeric 0 is always a value (0 !== "0"),
    // so counters like passes_for_today_count still update correctly.
    if (key === "student_sis_id" && value === "0") {
      return
    }
    // Treat null/undefined/"" as "no value" for every field; otherwise keep the
    // value already in the store rather than blanking it with an empty payload.
    // Use hasOwnProperty (not `in`) so inherited names like `toString` aren't
    // mistaken for an existing user field.
    const hasValue = value !== null && value !== undefined && value !== ""
    const keyAlreadyPresent = Object.prototype.hasOwnProperty.call(merged, key)
    if (hasValue || !keyAlreadyPresent) {
      merged[key] = value
    }
  })
  return merged
}

/**
 * Handles sorting of queue passes based on various columns
 * @param {Array} allQueuePasses - Array of all queue passes
 * @param {string} column - The column to sort by
 * @param {string} order - The order to sort by
 * @returns {Array} - Sorted array of queue passes
 */
const handleQueuePassSorting = (allQueuePasses, column, order) => {
  if (
    !["destination", "departing_from", "user", "queue_number"].includes(column)
  ) {
    return allQueuePasses
  }
  if (column === "user") {
    return (
      allQueuePasses
        ?.map?.((pass) => ({ ...pass }))
        ?.sort((a, b) => {
          return order === "asc"
            ? a?.user?.last_name?.localeCompare(b?.user?.last_name)
            : b?.user?.last_name?.localeCompare(a?.user?.last_name)
        }) || []
    )
  }
  if (column === "destination") {
    // destination means to
    return (
      allQueuePasses
        ?.map?.((pass) => ({ ...pass }))
        ?.sort((a, b) => {
          return order === "asc"
            ? a?.to?.name?.localeCompare(b?.to?.name)
            : b?.to?.name?.localeCompare(a?.to?.name)
        }) || []
    )
  }
  if (column === "departing_from") {
    // departing_from means from
    return (
      allQueuePasses
        ?.map?.((pass) => ({ ...pass }))
        ?.sort((a, b) => {
          return order === "asc"
            ? a?.from?.name?.localeCompare(b?.from?.name)
            : b?.from?.name?.localeCompare(a?.from?.name)
        }) || []
    )
  }
  if (column === "queue_number") {
    return (
      allQueuePasses
        ?.map?.((pass) => ({ ...pass }))
        ?.sort((a, b) => {
          return order === "asc"
            ? a?.queue_number - b?.queue_number
            : b?.queue_number - a?.queue_number
        }) || []
    )
  }
}

/**
 * Handles filtering of queue passes based on various filter criteria
 * @param {Array} allQueuePasses - Array of all queue passes
 * @param {Object} filterData - Filter data object containing various filter criteria
 * @returns {Array} - Filtered array of queue passes
 */
const handleQueuePassFiltering = (allQueuePasses, filterData) => {
  let filteredPasses = [...allQueuePasses]

  // filter by search query
  if (filterData?.search_query) {
    filteredPasses = filteredPasses.filter(
      (pass) =>
        pass?.user?.first_name
          .toLowerCase()
          .includes(filterData?.search_query?.toLowerCase()) ||
        pass?.user?.last_name
          .toLowerCase()
          .includes(filterData.search_query.toLowerCase())
    )
  }

  // filter by by_students
  if (filterData?.by_students?.length) {
    filteredPasses = filteredPasses.filter((pass) =>
      filterData?.by_students
        ?.map((student) => Number(student?.value?.id))
        ?.includes(Number(pass?.user_id))
    )
  }

  // filter by by_teachers
  if (filterData?.by_teachers?.length) {
    filteredPasses = filteredPasses.filter((pass) =>
      filterData?.by_teachers
        ?.map((teacher) => Number(teacher?.value?.id))
        ?.includes(
          (pass?.from_type === "App\\Models\\User" && Number(pass?.from_id)) ||
            (pass?.to_type === "App\\Models\\User" && Number(pass?.to_id))
        )
    )
  }

  // filter by by_rooms
  if (filterData?.by_rooms?.length) {
    filteredPasses = filteredPasses.filter((pass) =>
      filterData?.by_rooms
        ?.map((room) => Number(room?.value?.id))
        ?.includes(
          (pass?.to_type === "App\\Models\\Room" && Number(pass?.to_id)) ||
            (pass?.from_type === "App\\Models\\Room" && Number(pass?.from_id))
        )
    )
  }

  return filteredPasses
}

const state = {
  stats: {},
  unavailable: null,
  updateQueuePassesTable: false, // if true, then update the queue passes table
  queuePasses: new Map(),
  passes: new Map(),
  autoPassesRooms: null,
  staffSchedules: null,
  socketCounter: 0,
  usersAssignedRooms: {
    rooms: [],
    users: []
  },
  allowedInActivePasses: [],
  isActiveDashboardUpdate: true,
  listStyle: "list", //list, grid
  type: getAndSetPassTypesFromLocalStorage(), //my, all, appointments
  status: getAndSetPassStatusFromLocalStorage(), // both, ended, active, queue
  filterData: {
    type: "my", //my, all, appointments
    status: "active", // both, ended, active, queue
    search_query: null, // keyword
    sort: null, // sort by column:acs,desc
    by_students: [], //Ids
    by_teachers: [], //Ids
    by_rooms: [], //Ids
    waiting_approval: false,
    extended_time: false,
    system_ended: false,
    edited: false,
    yellow_time: false,
    global_search: false,
    student_sis_id: false,
    late_to_school: false,
    late_to_class: false,
    limit_exceeded: false,
    hallway: false
  },
  aptFilterData: {
    student_sis_id: false
  },
  passBlock: {},
  activeEditablePass: null,
  lockedStudentAttempts: null,
  isInitialPassTableDataFetched: false
}

const getters = {
  isInitialPassTableDataFetched(state) {
    return state.isInitialPassTableDataFetched
  },
  stats(state) {
    return state.stats
  },
  passes(state) {
    const allPasses = Array.from(state.passes.values())
    let filteredPasses = []
    const filters = {
      waiting_approval: (pass) => pass.badge_flags.waiting_approval == true,
      extended_time: (pass) => pass.badge_flags.extended == true,
      system_ended: (pass) => pass.badge_flags.system_completed == true,
      edited: (pass) => pass.badge_flags.edited == true,
      yellow_time: (pass) =>
        pass.badge_flags.min_time == true &&
        pass.badge_flags.system_completed == false,
      limit_exceeded: (pass) => pass?.badge_flags?.limit_exceeded === true,
      late_to_class: (pass) => pass?.tardy_badge === "LTC",
      late_to_school: (pass) => pass?.tardy_badge === "LTS",
      hallway: (pass) => helpers.isHallwayPass(pass)
    }
    if (
      !state.filterData.waiting_approval &&
      !state.filterData.system_ended &&
      !state.filterData.extended_time &&
      !state.filterData.edited &&
      !state.filterData.yellow_time &&
      !state.filterData.late_to_school &&
      !state.filterData.late_to_class &&
      !state.filterData.limit_exceeded &&
      !state.filterData.hallway
    ) {
      filteredPasses = [...allPasses]
    } else {
      Object.keys(filters).forEach((key) => {
        if (state.filterData[key]) {
          filteredPasses = [
            ...filteredPasses,
            ...allPasses.filter(filters[key])
          ]
        }
      })
      filteredPasses = [
        ...new Map(filteredPasses.map((item) => [item["id"], item])).values()
      ]
    }
    if (state.type === "my") {
      filteredPasses = filteredPasses.filter((pass) => helpers.isMyPass(pass))
    }

    return filteredPasses
      .filter((a) => helpers.filterByTab(a) && a.badge_flags.missed == false)
      .sort((a, b) => helpers.sortPassesByColumn(state.filterData.sort, a, b))
  },
  queuePasses(state) {
    let allQueuePasses = Array.from(state.queuePasses.values())
    allQueuePasses = handleQueuePassFiltering(allQueuePasses, state.filterData)
    if (state?.filterData?.sort) {
      const [column, order] = state?.filterData?.sort?.split(":") || ["", ""]
      if (column && order) {
        allQueuePasses = handleQueuePassSorting(allQueuePasses, column, order)
      }
    }
    return allQueuePasses
  },
  updateQueuePassesTable(state) {
    return state.updateQueuePassesTable
  },
  autoPassesRooms(state) {
    return state.autoPassesRooms
  },
  staffSchedules(state) {
    return state.staffSchedules
  },
  unavailable(state) {
    return state.unavailable
  },
  formListAutoPassesRooms(state) {
    if (state.autoPassesRooms) {
      return state.autoPassesRooms.map((room) => {
        return {
          value: { id: room.room.id, type: "App\\Models\\Room" },
          type: "App\\Models\\Room",
          id: room.room.id,
          label: room.room.name
        }
      })
    }
  },
  usersAssignedRooms(state) {
    return state.usersAssignedRooms
  },
  isActiveDashboardUpdate(state) {
    return state.isActiveDashboardUpdate
  },
  getListStyle(state) {
    return state.listStyle
  },
  isActivePassStatus(state) {
    return state.isActivePassStatus
  },
  filterData(state) {
    return state.filterData
  },
  aptFilterData(state) {
    return state.aptFilterData
  },
  getType(state) {
    return state.type
  },
  getStatus(state) {
    return state.status
  },
  passBlock(state) {
    return state.passBlock
  },
  allowedInActivePasses(state) {
    return state.allowedInActivePasses
  },
  activeEditablePass(state) {
    return state.activeEditablePass
  },
  lockedStudentAttempts(state) {
    return state.lockedStudentAttempts
  },
  isQueueStatusSelected(state) {
    return state.status === "queue"
  },
  isQueueStatusAndMyTypeSelected(state) {
    return state.status === "queue" && state.type === "my"
  },
  isQueueStatusAndAllTypeSelected(state) {
    return state.status === "queue" && state.type === "all"
  },
  isQueueActiveAndStatusSelected(_state, getters, _rootState, rootGetters) {
    const isQueueModuleActive = rootGetters["queueSettings/isQueueModuleActive"]
    const isQueueStatusSelected = getters.isQueueStatusSelected
    return isQueueModuleActive && isQueueStatusSelected
  }
}
const mutations = {
  SET_STATS(state, statsData) {
    state.stats = { ...state.stats, ...statsData }
  },
  SET_UNAVAILABLE(state, unavailable) {
    state.unavailable = unavailable
  },
  SET_PASSES(state, passesCollection) {
    state.passes = new Map()
    passesCollection.forEach((passObj) => state.passes.set(passObj.id, passObj))
  },
  SET_QUEUE_PASSES(state, queuePassesCollection) {
    if (!state.isActiveDashboardUpdate) {
      return
    }
    state.queuePasses = new Map()
    queuePassesCollection.forEach((passObj) => {
      const processedPass = processQueuePass(passObj)
      state.queuePasses.set(processedPass.id, processedPass)
    })
  },
  UPDATE_QUEUE_PASSES(state, queuePassesCollection) {
    if (!state.isActiveDashboardUpdate) {
      return
    }
    const queuePassesClone = new Map(state.queuePasses)
    queuePassesCollection.forEach((passObj) => {
      const processedPass = processQueuePass({ ...passObj })
      queuePassesClone.set(processedPass.id, processedPass)
    })
    state.queuePasses = queuePassesClone
  },
  UPDATE_PASSES(state, passesCollection) {
    passesCollection.forEach((passObj) => state.passes.set(passObj.id, passObj))
    state.passes = new Map(state.passes)
  },
  SET_AUTO_PASSES_ROOMS(state, data) {
    state.autoPassesRooms = data
  },
  SET_STAFF_SCHEDULES(state, data) {
    state.staffSchedules = data
  },
  UPDATE_AUTO_PASS_LIMIT(state, passLimit) {
    if (state.staffSchedules) {
      const newCollection = state.staffSchedules.map((limit) => {
        if (
          (limit.room_id && limit.room_id == passLimit.room.id) ||
          (limit.room && limit.room.id == passLimit.room.id)
        ) {
          return passLimit
        }
        return limit
      })
      state.staffSchedules = newCollection
    }
  },
  UPDATE_AUTO_PASS_LIMIT_LISTENER(state, passLimit) {
    if (state.staffSchedules) {
      const newCollection = state.staffSchedules.map((limit) => {
        if (
          (limit.room_id && limit.room_id == passLimit.locationId) ||
          (limit.room && limit.room.id == passLimit.locationId)
        ) {
          return {
            ...limit,
            limit:
              passLimit.limit != null || passLimit.limit == -1
                ? passLimit.limit
                : "-",
            room_advance: {
              ...limit.room_advance,
              limit:
                passLimit.limit != null || passLimit.limit == -1
                  ? passLimit.limit
                  : "-"
            }
          }
        }
        return { ...limit }
      })
      state.staffSchedules = newCollection
    }
  },
  UPDATE_PASS(state, passObj) {
    // Merge over the pass we already have instead of replacing it outright.
    // Some endpoints (e.g. the pass-history edit-time response used when editing
    // an ended pass) return a leaner payload that omits fields like
    // `user.student_sis_id`. A full replace would silently drop those, blanking
    // the Student Number column.
    //
    // Two different merge rules apply:
    // - Top-level pass fields: incoming values win; anything the payload omits
    //   falls back to what is already in the store.
    // - `user` object: fields are preserved when the incoming value is empty
    //   ("" / null / undefined, plus "0" for student_sis_id), so a lean payload
    //   can't blank out the Student Number column (see mergeUserPreservingValues).
    const existingPass = state.passes.get(passObj.id) || null
    let mergedPass = passObj
    if (existingPass) {
      mergedPass = { ...existingPass, ...passObj }
      if (existingPass.user || passObj.user) {
        // Keep the user identity we already have and only overlay incoming user
        // fields that carry a real value. The edit-time response returns the
        // user object but leaves identity fields like `student_sis_id` empty,
        // so a plain spread (incoming wins) would still blank them out.
        mergedPass.user = mergeUserPreservingValues(
          existingPass.user,
          passObj.user
        )
      }
    }
    mergedPass.badge_flags = helpers.getBadgeFlags(mergedPass)
    mergedPass.total_time = helpers.getTotalTime(mergedPass)
    // setUserAssignedRoomsPerPass mutates nested objects in place (assigns
    // `user_ids_assigned` onto pass.from, pass.to and pass.child.from/to). After
    // the shallow spread above those nested objects can still be shared with the
    // previously-stored pass, so clone them first to avoid mutating the old
    // object (or any other holder of those references).
    if (mergedPass.from) {
      mergedPass.from = { ...mergedPass.from }
    }
    if (mergedPass.to) {
      mergedPass.to = { ...mergedPass.to }
    }
    if (mergedPass.child) {
      mergedPass.child = {
        ...mergedPass.child,
        ...(mergedPass.child.from && { from: { ...mergedPass.child.from } }),
        ...(mergedPass.child.to && { to: { ...mergedPass.child.to } })
      }
    }
    mergedPass = helpers.setUserAssignedRoomsPerPass(mergedPass)
    if (!state.isActiveDashboardUpdate) {
      if (state.passes.has(passObj.id)) {
        state.passes.set(passObj.id, mergedPass)
      }
    } else {
      state.passes.set(passObj.id, mergedPass)
    }
    state.passes = new Map(state.passes)
  },
  SET_USERS_ASSIGNED_ROOMS(state, data) {
    const inverted = {}
    Object.keys(data).forEach((e) => {
      data[e].forEach((a) => {
        if (!inverted[a]) {
          inverted[a] = [parseInt(e)]
        } else {
          inverted[a].push(parseInt(e))
        }
      })
    })
    state.usersAssignedRooms = {
      users: data,
      rooms: inverted
    }
  },
  SET_DASHBOARD_LIST_STYLE(state, data) {
    state.listStyle = data
  },
  SET_ARCHIVED_USERS_DUE_TO_WRONG_PIN(state, data) {
    state.lockedStudentAttempts = data
  },
  SET_DASHBOARD_UPDATES(state, data) {
    state.isActiveDashboardUpdate = data
    if (data == false) {
      state.allowedInActivePasses = Array.from(state.passes.values())
        .filter(
          (pass) =>
            pass.pass_status == 1 || (pass.child && pass.child.pass_status)
        )
        .map((e) => e.id)
    } else {
      state.allowedInActivePasses = []
    }
  },
  SET_PASS_BLOCK(state, data) {
    let blocked = []
    if (data) {
      blocked = data.map((item) => {
        const el = {}
        const itemsBlocked = []
        item.students ? itemsBlocked.push("Student Pass") : ""
        item.kiosk ? itemsBlocked.push("Kiosk pass") : ""
        item.appointments ? itemsBlocked.push("Appointment pass") : ""
        item.proxy ? itemsBlocked.push("Teacher Pass") : ""
        el.items = itemsBlocked
        el.reason = item.reason
        el.to_date = item.to_date
        return el
      })
    }
    state.passBlock = blocked
  },
  UPDATE_TYPE(state, data) {
    state.type = data
    state.filterData.search_query = ""
    window.localStorage.setItem(
      passConstants.LOCAL_STORAGE_KEYS.DASHBOARD_PASS_TYPE,
      data
    )
  },
  UPDATE_STATUS(state, data) {
    state.status = data
    state.filterData.waiting_approval = false
    state.filterData.extended_time = false
    state.filterData.system_ended = false
    state.filterData.edited = false
    state.filterData.yellow_time = false
    state.filterData.late_to_class = false
    state.filterData.late_to_school = false
    state.filterData.limit_exceeded = false
    state.filterData.hallway = false
    state.filterData = JSON.parse(JSON.stringify(state.filterData))
    window.localStorage.setItem(
      passConstants.LOCAL_STORAGE_KEYS.DASHBOARD_PASS_STATUS,
      data
    )
  },

  UPDATE_FILTERS_DATA(state, elements) {
    if (elements instanceof Array) {
      elements.forEach((data) => {
        state.filterData[data.filter] = data.value
      })
    } else {
      state.filterData[elements.filter] = elements.value
    }
  },
  UPDATE_APT_FILTERS_DATA(state, elements) {
    if (elements instanceof Array) {
      elements.forEach((data) => {
        state.aptFilterData[data.filter] = data.value
      })
    } else {
      state.aptFilterData[elements.filter] = elements.value
    }
  },
  DECREMENT_STAT(state, type) {
    let statsValue = type
    if (type === "appointments_awaiting") {
      statsValue = "awaitingTodayAppointmentsCount"
    }
    if (type === "appointments_future_awaiting") {
      statsValue = "futureAppointmentsCount"
    }
    if (state.stats[statsValue] > 0) {
      state.stats[statsValue]--
    }
  },
  INCREMENT_STAT(state, type) {
    state.stats[type]++
  },
  SET_ACTIVE_EDITABLE_PASS(state, pass) {
    state.activeEditablePass = pass
  },
  SET_IS_INITIAL_PASS_TABLE_DATA_FETCHED(state, data) {
    state.isInitialPassTableDataFetched = data
  },
  SET_UPDATE_QUEUE_PASSES_TABLE(state, data) {
    state.updateQueuePassesTable = data
  }
}

const actions = {
  getDashboard(context) {
    return new Promise((resolve, reject) => {
      axios
        .get("/admin/users/dashboard")
        .then((response) => {
          resolve(response)
          const data = response.data.data
          if (data.students && data.students.length) {
            context.commit("users/SET_USERS", data.students, { root: true })
          }
          if (Object.prototype.hasOwnProperty.call(data, "stats")) {
            context.commit("SET_STATS", data.stats)
          }
          if (Object.prototype.hasOwnProperty.call(data, "autoPassesRooms")) {
            context.commit("SET_AUTO_PASSES_ROOMS", data.autoPassesRooms)
          }
          if (Object.prototype.hasOwnProperty.call(data, "staffSchedules")) {
            context.commit("SET_STAFF_SCHEDULES", data.staffSchedules)
          }
          if (Object.prototype.hasOwnProperty.call(data, "passBlocks")) {
            context.commit("SET_PASS_BLOCK", data.passBlocks)
          }
          if (
            Object.prototype.hasOwnProperty.call(data, "lockedStudentAttempts")
          ) {
            context.commit(
              "SET_ARCHIVED_USERS_DUE_TO_WRONG_PIN",
              data.lockedStudentAttempts
            )
          }
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  // Not used in the codebase, use getDashboardPassesData
  getDashboardPassesActiveData(context) {
    return new Promise((resolve, reject) => {
      const params = {
        type: "all", //my, all, appointments
        status: "active" // both, ended, active
      }
      params.search_query = context.state.filterData.search_query
      params.by_rooms = context.state.filterData.by_rooms.map((e) => e.value.id)
      params.by_students = context.state.filterData.by_students.map(
        (e) => e.value.id
      )
      params.by_teachers = context.state.filterData.by_teachers.map(
        (e) => e.value.id
      )
      apiV6nocache
        .get("/admin/users/dashboard/filter", {
          params: params
        })
        .then((response) => {
          let data = response.data.data
          const dataFromSocket = Array.from(state.passes.values())

          if (data && dataFromSocket) {
            const socketDataMap = new Map(
              dataFromSocket.map((item) => [item.id, item])
            )
            data = data.map((el) => socketDataMap.get(el.id) || el)
            data = JSON.parse(JSON.stringify(data))
          }
          if (data) {
            data = data.map((e) => {
              const isExceeding = helpers.isPassExceedingSystemEndTime(e)
              if (isExceeding) {
                const completeTime = helpers.getPassEndTime(e)
                const target = e.child || e
                target.pass_status = 0
                target.system_completed = 1
                target.completed_at = completeTime
              }
              e.badge_flags = helpers.getBadgeFlags(e)
              e.total_time = helpers.getTotalTime(e)
              return helpers.setUserAssignedRoomsPerPass(e)
            })
          }
          context.commit("UPDATE_PASSES", data)
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  getDashboardPassesData(context, payload) {
    const isInitialLoad = payload?.isInitialLoad || false
    const skipSocketMerge = payload?.skipSocketMerge || false
    return new Promise((resolve, reject) => {
      const params = {}
      params.type =
        payload?.type ||
        context?.state?.type ||
        getAndSetPassTypesFromLocalStorage()
      params.status =
        payload?.status ||
        context?.state?.status ||
        getAndSetPassStatusFromLocalStorage()
      params.search_query = context.state.filterData.search_query
      params.by_rooms = context.state.filterData.by_rooms.map((e) => e.value.id)
      params.by_students = context.state.filterData.by_students.map(
        (e) => e.value.id
      )
      params.by_teachers = context.state.filterData.by_teachers.map(
        (e) => e.value.id
      )
      if (context.state.filterData.global_search) {
        params.global_search = context.state.filterData.global_search
      }
      if (isInitialLoad) {
        context.commit("SET_IS_INITIAL_PASS_TABLE_DATA_FETCHED", false)
      }
      apiV6nocache
        .get("/admin/users/dashboard/filter", {
          params: params
        })
        .then((response) => {
          let data = response.data.data
          if (!skipSocketMerge) {
            const dataFromSocket = Array.from(state.passes.values())

            if (data && dataFromSocket) {
              const socketDataMap = new Map(
                dataFromSocket.map((item) => [item.id, item])
              )
              data = data.map((el) => socketDataMap.get(el.id) || el)
              data = JSON.parse(JSON.stringify(data))
            }
          }
          if (data) {
            data = data.map((e) => {
              const isExceeding = helpers.isPassExceedingSystemEndTime(e)
              if (isExceeding) {
                const completeTime = helpers.getPassEndTime(e)
                const target = e.child || e
                target.pass_status = 0
                target.system_completed = 1
                target.completed_at = completeTime
              }
              e.badge_flags = helpers.getBadgeFlags(e)
              e.total_time = helpers.getTotalTime(e)
              return helpers.setUserAssignedRoomsPerPass(e)
            })
          }
          context.commit(
            "dashboardCounters/SET_PASSES_FROM_DASHBOARD_TABLE",
            data,
            { root: true }
          )
          context.commit("SET_PASSES", data)
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
        .finally(() => {
          if (isInitialLoad) {
            context.commit("SET_IS_INITIAL_PASS_TABLE_DATA_FETCHED", true)
          }
        })
    })
  },
  getDashboardQueuePassesData(context, payload) {
    const params = {}
    if (payload?.type === "all") {
      params.all = 1
    }
    return new Promise((resolve, reject) => {
      queueApiV1NoCache
        .get("/passes", {
          params: params
        })
        .then((response) => {
          const data = response?.data?.data?.items || []
          context.commit("SET_QUEUE_PASSES", data)
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  clearQueuePasses(_context, payload) {
    return new Promise((resolve, reject) => {
      queueApiV1NoCache
        .delete(payload?.type === "all" ? "/passes/clear-all" : "/passes/clear")
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  getDashboardPassesExport(context) {
    const params = JSON.parse(JSON.stringify(context.state.filterData))
    params.type = context.state.type
    params.status = context.state.status
    return new Promise((resolve, reject) => {
      apiV6nocache
        .get("/admin/users/dashboard/export/csv", {
          params: params
        })
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  getUsersAssignedRooms(context) {
    return new Promise((resolve, reject) => {
      api
        .get("/admin/rooms/assign?for_dashboard=true")
        .then((response) => {
          context.commit("SET_USERS_ASSIGNED_ROOMS", response.data.data)
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  clearExceedingPasses() {
    return new Promise((resolve, reject) => {
      apiV6nocache
        .post("/admin/users/dashboard/clear-exceeding-passes")
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  refetchPassState(context, { passId }) {
    if (!passId) {
      return Promise.resolve(null)
    }

    return context
      .dispatch("passes/getPassById", passId, { root: true })
      .then((pass) => {
        if (!pass?.id) {
          return null
        }

        const isExceeding = helpers.isPassExceedingSystemEndTime(pass)
        if (isExceeding) {
          const completeTime = helpers.getPassEndTime(pass)
          const target = pass.child || pass
          target.pass_status = 0
          target.system_completed = 1
          target.completed_at = completeTime
        }
        pass.badge_flags = helpers.getBadgeFlags(pass)
        pass.total_time = helpers.getTotalTime(pass)
        const normalizedPass = helpers.setUserAssignedRoomsPerPass(pass)

        context.commit("UPDATE_PASS", normalizedPass)
        context.commit("dashboardCounters/UPDATE_PASS", normalizedPass, {
          root: true
        })
        return normalizedPass
      })
  },
  refetchAfterSocketReconnect({ state, dispatch, rootGetters }) {
    const activeRoute = rootGetters["routerDependencies/activeRoute"]
    if (activeRoute?.path !== "/dashboard") {
      return Promise.resolve()
    }

    const userRole = rootGetters["authentication/getUserRole"]
    if (userRole === "student") {
      return Promise.resolve()
    }

    const type = state.type
    const status = state.status
    const appointmentTypes = [
      "appointments_today",
      "appointments_awaiting",
      "appointments_future_awaiting"
    ]
    const isAppointmentMode = type?.includes?.("appointment")
    const isQueueActive =
      rootGetters["dashboardTable/isQueueActiveAndStatusSelected"]
    const isActiveAppoinmentModule =
      rootGetters["schools/isActiveAppoinmentModule"]

    const refetches = [
      dispatch("dashboardCounters/getAllCounters", null, { root: true }),
      dispatch("dashboardCounters/getPassesForCounters", null, { root: true }),
      dispatch("getDashboard")
    ]

    if (isActiveAppoinmentModule) {
      refetches.push(
        dispatch("dashboardCounters/getAppointmentMetricsCount", null, {
          root: true
        }),
        dispatch("dashboardCounters/getAppointmentsCounters", null, {
          root: true
        })
      )
    }

    if (isActiveAppoinmentModule && isAppointmentMode) {
      refetches.push(
        dispatch("adultAptPass/getAppointmentPasses", null, { root: true })
      )
    } else if (isQueueActive) {
      refetches.push(
        dispatch("getDashboardQueuePassesData", {
          type: type === "all" ? "all" : undefined
        })
      )
    } else if (!appointmentTypes.includes(type)) {
      refetches.push(
        dispatch("getDashboardPassesData", {
          type,
          status,
          isInitialLoad: false,
          skipSocketMerge: true
        })
      )
    }

    // eslint-disable-next-line no-console
    console.log(`Socket is fetching ${refetches.length} dashboard APIs`)

    return Promise.allSettled(refetches)
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
